import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API } from '@/config/api';
import { clearImpersonationStorage } from '@/utils/impersonation';
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set auth header from localStorage token
  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    const config = {};
    if (token) {
      config.headers = { Authorization: `Bearer ${token}` };
    }
    return config;
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, getAuthConfig());
      setUser(data);
    } catch {
      localStorage.removeItem('auth_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [getAuthConfig]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Set default auth header for all requests
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    // Store token in localStorage for cross-domain support
    if (data.token) {
      localStorage.setItem('auth_token', data.token);
    }
    setUser(data);
    // Fetch the richer /auth/me payload so recruiter-scoped flags
    // (declaration_form_enabled, hide_commission) are available immediately
    try { await checkAuth(); } catch {}
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, getAuthConfig());
    } catch {}
    localStorage.removeItem('auth_token');
    clearImpersonationStorage();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
